import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import { Box, Button, Skeleton, IconButton, Menu, MenuItem, FormControl, InputLabel } from '@mui/material'
import { useQuery } from 'react-query'
import { TextInput, useListContext, useTranslate, BooleanInput } from 'react-admin'
import ContentFilter from '@mui/icons-material/FilterList'
import isEqual from 'lodash/isEqual'
import { roleServices } from 'src/services/auth/rol/rol.service'
import { SelectInput } from '../fields/SelectInput'
import { HighlightOff } from '@mui/icons-material'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'
import { workUnitServices } from 'src/services/reservation/workUnit/WorkUnit.service'

import get from 'lodash/get'
import { TreeViewInput } from './PostFilterButtonReservation'

export const PostFilterButtonUser = ({ listFilters }) => {
  const { displayedFilters = {}, setFilters, showFilter } = useListContext()
  const translate = useTranslate()
  const hasDisplayedFilters = !isEqual(displayedFilters, {})
  const [open, setOpen] = useState(false)
  const anchorEl = useRef()

  const handleClickButton = useCallback(
    (event) => {
      // This prevents ghost click.
      event.preventDefault()
      setOpen(true)
      anchorEl.current = event.currentTarget
    },
    [anchorEl, setOpen]
  )

  const handleRequestClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleShow = useCallback(
    ({ source, defaultValue }) => {
      showFilter(source, defaultValue === '' ? undefined : defaultValue)
      setOpen(false)
    },
    [showFilter, setOpen]
  )

  return (
    <Box>
      <Button
        className='add-filter'
        label='ra.action.add_filter'
        aria-haspopup='true'
        onClick={handleClickButton}
        size='small'
      >
        <ContentFilter /> {translate('ra.action.add_filter')}
      </Button>
      <Menu open={open} anchorEl={anchorEl.current} onClose={handleRequestClose}>
        {Array.isArray(listFilters) &&
          listFilters.map(
            (item, index) =>
              !displayedFilters?.[item.source] && (
                <MenuItem
                  onClick={() => {
                    setOpen(false)
                    handleShow({ source: item.source })
                  }}
                  key={index}
                >
                  {translate(`${item.name}`)}
                </MenuItem>
              )
          )}

        {hasDisplayedFilters && (
          <MenuItem onClick={() => setFilters({}, {}, false)}>
            {translate('ra.action.remove_all_filters')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

const assignIdToValue = (objects, labelField) => {
  if (Array.isArray(objects)) {
    return objects.map((obj) => {
      const newObj = {
        id: obj?.id,
        fiscal_data_relation: obj?.fiscal_data_relation,
        label: obj?.label,
        token: obj?.token,
        fantasy_name: obj?.fantasy_name,
        short_name: obj?.short_name,
        long_name: obj?.long_name,
        value: obj?.value,
        children: obj?.children || null,
      }

      // Usar 'get' para acceder a propiedades anidadas
      newObj.label = labelField
        ?.map((field) => get(obj, field))
        .filter((elem) => elem)
        .join(' - ')
      if (newObj.id !== undefined) {
        newObj.value = String(newObj.id) + '__' + newObj.label
      }

      if (newObj.children && newObj.children.length > 0) {
        newObj.children = assignIdToValue(newObj.children, labelField)
      } else {
        newObj.children = null
      }

      return newObj
    })
  }
  return null
}

const getDefaultValueTreeView = (data, value) => {
  let valueAux = []
  data.forEach((elem) => {
    if (elem.id === value) {
      valueAux.push(elem.value)
    } else if (elem.children && elem.children.length > 0) {
      valueAux = valueAux.concat(getDefaultValueTreeView(elem.children, value))
    }
  })

  return valueAux
}

const filterTreeView = (data, value, labelField) => {
  let expanded = []

  if (value) {
    const dataAux = []
    data.forEach((elem) => {
      const [filteredChildren, expandedChildren] = elem.children
        ? filterTreeView(elem.children, value, labelField)
        : [[], []]
      if (filteredChildren.length > 0) {
        elem.children = filteredChildren
        dataAux.push(elem)
        expanded = expanded.concat([elem.value, expandedChildren]).flat()
      } else if (elem[labelField]?.toLowerCase()?.includes(value)) {
        dataAux.push(elem)
      }
    })

    return [dataAux, expanded]
  }

  return [data, expanded]
}

const SelectArrayInputFn = ({
  queryName,
  label,
  optionsText = ['long_name'],
  optionValue,
  source,
  queryFn,
  ...props
}) => {
  const options = useQuery(queryName, () => queryFn())
  return options.isLoading ? (
    <Skeleton variant='rectangular' width={'270px'} height={'40px'} />
  ) : (
    <FormControl fullWidth className='select-array-input'>
      {label && <InputLabel>{label}</InputLabel>}
      <SelectInput
        source={source}
        options={options?.data || []}
        optionsText={optionsText}
        optionValue={optionValue}
        multiple
        showSearch
        {...props}
      />
    </FormControl>
  )
}

const RemoveFilterField = ({ children, prop, fields, width = null, maxWidth = null, minWidth = null }) => {
  const { displayedFilters = {}, setFilters } = useListContext()
  const { setValue, getValues } = useFormContext()

  const onClickButton = () => {
    const copy = { ...displayedFilters }
    delete copy[prop]

    if (Array.isArray(fields) && fields.length > 0) {
      fields.forEach((field) => setValue(field, undefined))

      let values = getValues()
      if (values.business_unit__country_id__in) {
        values.business_unit__country_id__in = values.business_unit__country_id__in.map((elem) => elem.id)
      }
      setFilters(values, copy, false)
    }
  }

  return (
    <Box
      display={'flex'}
      alignItems={'flex-start'}
      marginRight={5}
      width={width}
      maxWidth={maxWidth}
      minWidth={minWidth}
    >
      <Box width={'34px'} paddingTop={1}>
        <IconButton size='small' onClick={onClickButton}>
          <HighlightOff />
        </IconButton>
      </Box>
      <Box width={'calc(100% - 34px)'} display={'flex'} minHeight={'60px'} height={'auto'}>
        {children}
      </Box>
    </Box>
  )
}



export const PostFilterFormUser = () => {
  const { displayedFilters = {}, filterValues, setFilters } = useListContext()
  const translate = useTranslate()
  const form = useForm()

  useEffect(() => {
    Object.entries(filterValues).forEach((entry) => {
      const [key, value] = entry
      form.setValue(key, value)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (displayedFilters && Object.keys(displayedFilters).length === 0) {
      form.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedFilters])

  const onSubmit = (values) => {
    if (Object.keys(values).length > 0) {
      if (values.business_unit__country_id__in) {
        values.business_unit__country_id__in = values.business_unit__country_id__in.map((elem) => elem.id)
      }
      setFilters(values, displayedFilters, false)
    }
  }

  const resetFilter = () => {
    setFilters({}, {}, false)
  }

  const areFilterActivate = (displayedFilters) => {
    if (displayedFilters && Object.keys(displayedFilters).length > 0) {
      const props = Object.values(displayedFilters)
      return props.some((prop) => prop === true)
    }
    return false
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center' mb={1} height={'auto'}>
          <Box width={'calc(100% - 156px)'} display={'flex'} flexWrap={'wrap'}>
            {displayedFilters?.id && (
              <RemoveFilterField prop={'id'} fields={['id']} width={300}>
                <TextInput label={translate('ra.field.id')} fullWidth source='id' />
              </RemoveFilterField>
            )}
            {displayedFilters?.username && (
              <RemoveFilterField prop={'username'} fields={['username']} width={300}>
                <TextInput label={translate('ra.field.username')} fullWidth source='username' />
              </RemoveFilterField>
            )}
            {displayedFilters?.firstname && (
              <RemoveFilterField prop={'firstname'} fields={['firstname']} width={300}>
                <TextInput label={translate('ra.field.firstname')} fullWidth source='firstname' />
              </RemoveFilterField>
            )}
            {displayedFilters?.surname && (
              <RemoveFilterField prop={'surname'} fields={['surname']} width={300}>
                <TextInput label={translate('ra.field.surname')} fullWidth source='surname' />
              </RemoveFilterField>
            )}
            {displayedFilters?.mail && (
              <RemoveFilterField prop={'mail'} fields={['mail']} width={300}>
                <TextInput label={translate('ra.field.mail')} fullWidth source='mail' />
              </RemoveFilterField>
            )}
            {displayedFilters?.is_superadmin && (
              <RemoveFilterField prop={'is_superadmin'} fields={['is_superadmin']} width={300}>
                <BooleanInput label={translate('ra.field.is_superadmin')} fullWidth source='is_superadmin' />
              </RemoveFilterField>
            )}
            {displayedFilters?.is_staff && (
              <RemoveFilterField prop={'is_staff'} fields={['is_staff']} width={300}>
                <BooleanInput label={translate('ra.field.is_staff')} fullWidth source='is_staff' />
              </RemoveFilterField>
            )}
            {displayedFilters?.is_active && (
              <RemoveFilterField prop={'is_active'} fields={['is_active']} width={300}>
                <BooleanInput label={translate('ra.field.is_active')} fullWidth source='is_active' />
              </RemoveFilterField>
            )}
            {displayedFilters?.nrf_book && (
              <RemoveFilterField prop={'nrf_book'} fields={['nrf_book']} width={300}>
                <BooleanInput label={translate('ra.field.reserve_nrf')} fullWidth source='nrf_book' />
              </RemoveFilterField>
            )}
            {displayedFilters?.rol && (
              <RemoveFilterField prop={'rol'} fields={['rol']} width={300}>
                <TextInput label={translate('ra.field.rol')} fullWidth source='rol' />
              </RemoveFilterField>
            )}
            {displayedFilters?.rol__in && (
              <RemoveFilterField prop={'rol__in'} fields={['rol__in']} width={300}>
                <SelectArrayInputFn
                  source='rol__in'
                  queryName='rol-all'
                  label={translate('ra.single_entity.role')}
                  queryFn={roleServices.getByAll}
                  optionsText={['long_name']}
                  optionValue={'long_name'}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.business_unit_id__in && (
              <RemoveFilterField
                prop={'business_unit_id__in'}
                fields={['business_unit_id__in']}
                minWidth={300}
                maxWidth={600}
              >
                <TreeViewInput
                  queryName='business-unit-all'
                  fieldName='business_unit_id__in'
                  modalTitle='Selecciona una unidad de negocio'
                  buttonText={translate('ra.message.filter_by_business_unit')}
                  labelField={['fiscal_data_relation.name', 'fantasy_name']}
                  queryFn={businessUnitServices.getByOrderParent}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.work_unit_id__in && (
              <RemoveFilterField
                prop={'work_unit_id__in'}
                fields={['work_unit_id__in']}
                minWidth={300}
                maxWidth={600}
              >
                <TreeViewInput
                  queryName='work-unit-all'
                  fieldName='work_unit_id__in'
                  modalTitle={translate('ra.title.select_work_unit')}
                  buttonText={translate('ra.message.filter_by_work_unit')}
                  labelField={['label']}
                  queryFn={workUnitServices.getByOrderParent}
                />
              </RemoveFilterField>
            )}
          </Box>
          <Box
            width={'156px'}
            display={'flex'}
            flexWrap={'wrap'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
          >
            {areFilterActivate(displayedFilters) && (
              <>
                <Box mr={2} paddingY={'10px'}>
                  <Button variant='outlined' color='primary' type='submit'>
                    {translate('ra.action.filter')}
                  </Button>
                </Box>
                <Box paddingY={'10px'}>
                  <Button variant='outlined' onClick={resetFilter}>
                    {translate('ra.action.close')}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </form>
    </FormProvider>
  )
}
